import {ChangeDetectorRef, Component, OnDestroy, ViewChild} from '@angular/core';
import {NbMenuComponent, NbMenuItem, NbMenuService} from "@nebular/theme";
import {ProjectService} from "../shared/project.service";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {AuthService} from "../shared/auth.service";
import {SubscribedComponent} from "../subscribed.component";
import {User, UserRole} from "../interfaces/user";
import {TranslateService} from "@ngx-translate/core";
import {Globals} from "../shared/Globals";
import {TimeTrackingService} from "../shared/time-tracking.service";
import {filter} from "rxjs/operators";
import {MarketNavService} from "../shared/market-nav.service";

/** Market in the sidebar: a parent with Overview and Intake when Intake is on for this user, else one link. */
export function marketMenuItem(showIntake: boolean): NbMenuItem {
  if (!showIntake) {
    return {
      title: 'Market overview',
      link: '/market',
      icon: {icon: 'briefcase', pack: 'lucide'}
    };
  }
  return {
    title: 'Market',
    icon: {icon: 'briefcase', pack: 'lucide'},
    children: [
      {
        title: 'Overview',
        link: '/market/overview',
        icon: {icon: 'briefcase', pack: 'lucide'}
      },
      {
        title: 'Intake',
        link: '/market/intake',
        icon: {icon: 'inbox', pack: 'lucide'}
      }
    ]
  };
}

/** Child highlight: its own link, anything below it, and a vacancy detail belongs to Overview, or to Intake for a Direct request. */
export function menuChildSelected(url: string, link: string, directRequest = false): boolean {
  const owner = directRequest ? '/market/intake' : '/market/overview';
  return url === link || url.startsWith(link + '/') || (link === owner && url.startsWith('/market/vacancies/'));
}

@Component({
    selector: 'bst-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent extends SubscribedComponent implements OnDestroy {

  @ViewChild('menu') menu: NbMenuComponent;

  public items: NbMenuItem[] = [];
  public readonly Globals = Globals;
  private pendingApprovalCount = 0;

  constructor(private projectService: ProjectService,
              private menuService: NbMenuService,
              private auth: AuthService,
              private detector: ChangeDetectorRef,
              public translate: TranslateService,
              private route: ActivatedRoute,
              private router: Router,
              private timeTracking: TimeTrackingService,
              private marketNav: MarketNavService) {
    super();
  }

  year() {
    return new Date().getFullYear();
  }

  // TODO: Move to configservice
  title = 'teamer';

  private whatSidebar(){

    this.items = [];
    if(this.auth.loggedIn){
      if(!this.auth.isNew) {
        this.items = this.items.concat([
          {
            title: 'My profile',
            link: '/users/me',
            icon: {icon: 'user', pack: 'lucide'}
          },
          {
            title: this.translate.instant('registration.title'),
            icon: {icon: 'clock', pack: 'lucide'},
            data: {key: 'registration'},
            children: [
              {title: this.translate.instant('registration.hours'), link: '/registration/hours', icon: {icon: 'timer', pack: 'lucide'}},
              ...(this.auth.user.getValue()?.trips_enabled
                ? [{title: this.translate.instant('registration.km'), link: '/registration/kilometres', icon: {icon: 'car', pack: 'lucide'}}] : []),
            ],
          }
        ]);

        if (this.auth.user.getValue()?.dashboard_enabled) {
          this.items.push({
            title: 'Dashboard',
            link: '/dashboard',
            icon: {icon: 'trending-up', pack: 'lucide'}
          });
        }

        // Only surface Approvals to users who can actually review: staff/admin, or
        // anyone who currently has weeks waiting on them (catches non-staff managers).
        // Plain consultants with nothing to approve don't get the menu item.
        if (this.auth.isAdmin || this.auth.isStaffer || this.pendingApprovalCount > 0) {
          const approvals: NbMenuItem = {
            title: 'Approvals',
            link: '/time-approvals',
            icon: {icon: 'circle-check', pack: 'lucide'}
          };
          if (this.pendingApprovalCount) {
            approvals.badge = { status: 'info', text: this.pendingApprovalCount.toString() };
          }
          this.items.push(approvals);
        }

        if(this.auth.curClient) {
          this.items.push({
            title: 'Projects',
            link: '/projects',
            icon: {icon: 'package', pack: 'lucide'}
          });
        }

        // Admin or the Invoicing grant (#1518).
        if(this.auth.canInvoice && this.auth.user.getValue()?.invoicing_enabled) {
          this.items.push({
            title: 'Invoices',
            link: '/invoices',
            icon: {icon: 'receipt-euro', pack: 'lucide'}
          });
        }

        if(this.auth.isAdmin || this.auth.isStaffer || this.auth.isUser) {
          const showIntake = (this.auth.isAdmin || this.auth.isStaffer) && !!this.auth.user.getValue()?.intake_enabled;
          this.items.push(marketMenuItem(showIntake));
        }

        if((this.auth.isAdmin || this.auth.isStaffer) && this.auth.user.getValue()?.crm_enabled) {
          this.items.push({
            title: 'CRM',
            // Every icon in the curated set (assets/lucide.json) is
            // already assigned to something else; id-card is the least-bad
            // reuse — it's otherwise only nested two levels deep under
            // Directories > Project role, never visible alongside this at the
            // same time, unlike reusing Opportunities' own 'star'.
            icon: {icon: 'id-card', pack: 'lucide'},
            children: [
              {
                title: 'Opportunities',
                link: '/opportunities',
                icon: {icon: 'star', pack: 'lucide'}
              },
              {
                title: 'Submissions',
                link: '/submissions',
                icon: {icon: 'rocket', pack: 'lucide'}
              }
            ]
          });
        }
      }
      else {
        this.items = this.items.concat([
          {
            title: 'My profile',
            link: '/users/me',
            icon: {icon: 'user', pack: 'lucide'}
          }
        ]);

        // Always shown here, unlike the !isNew branch above: /dashboard is
        // this user's only self-serve onboarding page, independent of the
        // dashboard_enabled flag (that flag only gates the metrics view).
        this.items.push({
          title: 'Dashboard',
          link: '/dashboard',
          icon: 'home'
        });
      }

      if(this.auth.isAdmin || this.auth.isStaffer){
        this.items.push({
          title: 'Users',
          link: '/users',
          icon: {icon: 'users', pack: 'lucide'},
          // children: [
          //   {
          //     title: 'Active',
          //     link: '/users'
          //   },
          //   {
          //     title: 'Requests',
          //     link: '/requests',
          //     badge: {
          //       status: 'info',
          //       text: '3'
          //     }
          //   }
          // ]
        });
      }

      if(this.auth.isAdmin || this.auth.isStaffer){
        this.items.push({
          title: 'Clients',
          link: '/clients',
          icon: {icon: 'building-2', pack: 'lucide'}
        });

        // this.items.push({
        //   title: 'Schedules',
        //   icon: {icon: 'clock', pack: 'lucide'},
        //   children: [
        //     {
        //       title: 'By Project',
        //       link: '/schedule/projects'
        //     },
        //     {
        //       title: 'By User',
        //       link: '/schedule/users'
        //     },
        //     {
        //       title: 'By Client',
        //       link: '/schedule/clients'
        //     }
        //   ]
        // });
      }

      // Directories collects the reference lists. Organizations belongs here
      // rather than beside Opportunities: it is the shared catalogue of
      // companies seen in the market, not one of this tenant's own deals.
      //
      // Its audience is wider than the rest of the group's, though. Everything
      // below Organizations is admin-curated configuration, while a staffer
      // browses the catalogue while working, so the group is assembled from
      // what each role may actually reach and is only pushed if that leaves
      // anything in it. A staffer therefore sees a Directories menu holding
      // Organizations alone, which is preferable to the alternatives: nesting
      // it admin-only would silently take a nav entry away from the people who
      // use it most, and widening the whole group would offer staffers a dozen
      // admin config screens their role cannot open.
      //
      // crm_enabled stays on the entry even though a catalogue of companies is
      // not obviously a CRM feature. The screen is not self-contained: the
      // organization detail page asks for that organization's opportunities,
      // and that endpoint sits behind EnsureCrmEnabled. With the flag off the
      // request 404s, so the page would toast an error and then show an empty
      // Opportunities panel, which reads as "this company has no deals" rather
      // than "this feature is off". Dropping the flag here while moving the
      // item is what introduced that, and production runs with CRM off today.
      // Decoupling the two properly means suppressing that panel rather than
      // hiding the whole directory, and is deliberately not done here.
      const directories: NbMenuItem[] = [];

      if((this.auth.isAdmin || this.auth.isStaffer) && this.auth.user.getValue()?.crm_enabled){
        directories.push({
          title: 'Organizations',
          // 'landmark' isn't in this app's curated lucide pack (assets/lucide.json,
          // hand-registered icons only) and NbIconComponent renders nothing for
          // an unregistered name, silently, with no console error. 'network' used
          // to serve here, which stopped working once this moved into the group:
          // Department already carries it, and the two would now be siblings.
          // 'building-2' is shared with Clients, deliberately. Both are companies
          // and differ in scope rather than in kind, and they never sit adjacent.
          icon: {icon: 'building-2', pack: 'lucide'},
          link: '/organizations'
        });
      }

      if(this.auth.isAdmin){

        directories.push(
            {
              title: 'Questions',
              icon: {icon: 'circle-help', pack: 'lucide'},
              link: '/directory/questions'
            },
            {
              title: 'Department',
              icon: {icon: 'network', pack: 'lucide'},
              link: '/directory/departments'
            },
            {
              title: 'Seniority',
              icon: {icon: 'trending-up', pack: 'lucide'},
              link: '/directory/seniorities'
            },
            {
              title: 'Project role',
              icon: {icon: 'id-card', pack: 'lucide'},
              link: '/directory/projectroles'
            }
        );

        // These catalogs are global (Directory::clientSpecific() === false), so
        // DirectoryPolicy now requires superuser to manage them (#639) — a plain
        // tenant admin who follows one of these links gets a create/edit/delete
        // 403. Gated here rather than left for the page to reject, the same way
        // the rest of this menu is assembled from what the viewer may reach.
        if(this.auth.isSuperuser){
          directories.push(
              {
                title: 'Industry',
                icon: {icon: 'factory', pack: 'lucide'},
                link: '/directory/industries'
              },
              {
                title: 'Language',
                icon: {icon: 'languages', pack: 'lucide'},
                link: '/directory/languages'
              },
              {
                title: 'Qualification',
                icon: {icon: 'graduation-cap', pack: 'lucide'},
                link: '/directory/degrees'
              },
              {
                title: 'Hard skills',
                icon: {icon: 'code', pack: 'lucide'},
                link: '/directory/skills'
              },
              {
                title: 'Skill categories',
                icon: {icon: 'layers', pack: 'lucide'},
                link: '/directory/skill-categories'
              },
              {
                title: 'Soft skills',
                icon: {icon: 'smile', pack: 'lucide'},
                link: '/directory/developments'
              },
              {
                title: 'Certifications',
                icon: {icon: 'badge-check', pack: 'lucide'},
                link: '/directory/certifications'
              },
              {
                title: 'Country expertise',
                icon: {icon: 'globe', pack: 'lucide'},
                link: '/directory/markets'
              }
          );
        }

        this.items.push({
          title: 'My company',
          icon: {icon: 'building', pack: 'lucide'},
          link: '/company'
        });
      }

      // Pushed after both contributors have had their say, so the group holds
      // every entry the viewer may reach. Where it lands among the top-level
      // items is not decided here: menuOrder below sorts the finished list.
      if(directories.length){
        this.items.push({
          title: 'Directories',
          icon: {icon: 'book-open', pack: 'lucide'},
          children: directories
        });
      }

      if(this.auth.isAdmin || this.auth.isStaffer){
        this.items.push({
          title: 'CV generator',
          icon: {icon: 'file-text', pack: 'lucide'},
          link: '/cv-generator'
        });

        this.items.push({
          title: 'Archive bin',
          icon: {icon: 'archive', pack: 'lucide'},
          link: '/bin'
        });
      }

      // this.items.push({
      //   title: 'Logout',
      //   icon: null,
      //   link: '/logout'
      // });
    }
    else{
      this.items = this.items.concat([
        {
          title: 'Login',
          icon: {icon: 'user', pack: 'lucide'},
          link: '/login'
        },
        {
          title: 'Register',
          icon: {icon: 'user-plus', pack: 'lucide'},
          link: '/register'
        }
      ]);
    }
    // Purpose-grouped ordering: home -> me -> work pipeline -> tool -> company/
    // config -> bin. Reordering the already-built list keeps every item's role
    // gating above intact. Keyed by route so it survives show/hide; Directories
    // has no link. Dashboard leads when present (dark by default pending QC,
    // see DASHBOARD_ENABLED); /market is the actual post-login landing.
    const menuOrder = ['/dashboard', '/users/me', 'registration', '/time-approvals', '/market', 'Market',
      'CRM', '/projects', '/invoices', '/clients', '/users', '/cv-generator', '/company', 'Directories',
      '/bin', '/login', '/register'];
    const rank = (it: NbMenuItem): number => {
      const i = menuOrder.indexOf(it.data?.key ?? it.link ?? it.title);
      return i === -1 ? 999 : i;
    };
    this.items.sort((a, b) => rank(a) - rank(b));

    this.items.forEach(item => {
      item.pathMatch = 'full';
      const url = this.router.url.split('?')[0];
      if (item.link) {
        // Special case: /users/me should not highlight /users
        if (item.link === '/users' && url === '/users/me') {
          (item as any).selected = false;
        } else {
          (item as any).selected = url === item.link || url.startsWith(item.link + '/');
        }
      }
      if (item.children) {
        let childSelected = false;
        item.children.forEach(child => {
          child.pathMatch = 'full';
          if (child.link) {
            const isSelected = menuChildSelected(url, child.link, this.marketNav.directRequestOpen.getValue());
            (child as any).selected = isSelected;
            if (isSelected) { childSelected = true; }
          }
        });
        if (childSelected) {
          item.expanded = true;
        }
      }
    });

    this.detector.detectChanges();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.auth.user.subscribe((user: User) => {
      this.whatSidebar();
      void this.refreshApprovalCount();
    }));
    this.subs.push(this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.whatSidebar();
    }));
    // Re-fetch the badge live whenever an approval decision changes the queue,
    // so it never sits stale after the reviewer approves/rejects on this page.
    // The detail page learns its kind only after loading, i.e. after NavigationEnd rebuilt the menu.
    this.subs.push(this.marketNav.directRequestOpen.subscribe(() => this.whatSidebar()));
    this.subs.push(this.timeTracking.approvalsChanged.subscribe(() => void this.refreshApprovalCount()));
    // Only Registration's labels are translate.instant() today; this rebuild is what
    // keeps them current when the language switches (the header's Language menu triggers it).
    this.subs.push(this.translate.onLangChange.subscribe(() => this.whatSidebar()));
    this.whatSidebar();
    void this.refreshApprovalCount();
  }

  private async refreshApprovalCount(): Promise<void> {
    if (!this.auth.loggedIn) { this.pendingApprovalCount = 0; return; }
    try {
      this.pendingApprovalCount = (await this.timeTracking.pendingApprovalCount())?.count || 0;
    } catch {
      this.pendingApprovalCount = 0;
    }
    this.whatSidebar();
  }
}
